import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, StatCard, Button } from "@repo/o37-ui";
import {
  Building,
  Users,
  CreditCard,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  ArrowRight,
  FileSpreadsheet,
  Calculator,
  BarChart3,
  RefreshCw,
  AlertCircle
} from "lucide-react";
import {
  formatCents,
  getMonthlyEquivalent,
  getAnnualEquivalent,
  HoldingsBillingCycle
} from '../../types';
import RunRateWidget from '../../components/Holdings/RunRateWidget';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

interface OverviewData {
  companies: {
    total: number;
    active: number;
  };
  people: {
    total: number;
    active: number;
  };
  subscriptions: {
    total: number;
    active: number;
    monthlyRunRate: number;
    annualRunRate: number;
    upcomingRenewals: any[];
  };
  oneTimeCosts: {
    thisMonth: number;
    thisYear: number;
    lastMonth: number;
  };
  revenue: {
    thisYear: number;
    lastYear: number;
  };
  bankImports: {
    unmatchedTransactions: number;
  };
  plans: {
    draft: number;
    approved: number;
  };
  topCostsByCategory: { category: string; amount: number }[];
}

export default function HoldingsOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load all data in parallel
      const [
        companiesRes,
        peopleRes,
        subscriptionsRes,
        oneTimeCostsRes,
        thisYearPLRes,
        lastYearPLRes,
        transactionsRes,
        plansRes
      ] = await Promise.all([
        fetch(`${API_URL}/holdings/companies`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/people`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/one-time-costs`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
// Transaction-based revenue using company P&L endpoint (deprecating manual revenue entries)
        fetch(`${API_URL}/holdings/analytics/company-pl?startDate=${new Date().getFullYear()}-01-01&endDate=${new Date().getFullYear()}-12-31`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        // Last year's transaction-based revenue for YoY comparison
        fetch(`${API_URL}/holdings/analytics/company-pl?startDate=${new Date().getFullYear() - 1}-01-01&endDate=${new Date().getFullYear() - 1}-12-31`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/transactions/unmatched`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/plans`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      const companies = companiesRes.ok ? await companiesRes.json() : [];
      const people = peopleRes.ok ? await peopleRes.json() : [];
      const subscriptions = subscriptionsRes.ok ? await subscriptionsRes.json() : [];
      const oneTimeCosts = oneTimeCostsRes.ok ? await oneTimeCostsRes.json() : [];
      // Transaction-based revenue from company P&L (this is income from bank transactions)
      const thisYearPL = thisYearPLRes.ok ? await thisYearPLRes.json() : { totals: { income: 0, expenses: 0 } };
      const lastYearPL = lastYearPLRes.ok ? await lastYearPLRes.json() : { totals: { income: 0, expenses: 0 } };
      const unmatchedTransactions = transactionsRes.ok ? await transactionsRes.json() : [];
      const plans = plansRes.ok ? await plansRes.json() : [];

      // Calculate metrics
      const activeCompanies = companies.filter((c: any) => c.status === 'active');
      const activePeople = people.filter((p: any) => p.is_active);
      const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'active');

      // Run rates
      const monthlyRunRate = activeSubscriptions.reduce((sum: number, s: any) => {
        return sum + getMonthlyEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
      }, 0);
      const annualRunRate = activeSubscriptions.reduce((sum: number, s: any) => {
        return sum + getAnnualEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
      }, 0);

      // Upcoming renewals (next 30 days)
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      const upcomingRenewals = activeSubscriptions
        .filter((s: any) => {
          if (!s.renewal_date) return false;
          const renewalDate = new Date(s.renewal_date);
          return renewalDate >= now && renewalDate <= thirtyDaysFromNow;
        })
        .sort((a: any, b: any) => new Date(a.renewal_date).getTime() - new Date(b.renewal_date).getTime())
        .slice(0, 5);

      // One-time costs by period
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
      const startOfYear = new Date(now.getFullYear(), 0, 1);

      const thisMonthCosts = oneTimeCosts
        .filter((c: any) => new Date(c.date) >= startOfMonth)
        .reduce((sum: number, c: any) => sum + c.amount_cents, 0);

      const lastMonthCosts = oneTimeCosts
        .filter((c: any) => {
          const date = new Date(c.date);
          return date >= startOfLastMonth && date <= endOfLastMonth;
        })
        .reduce((sum: number, c: any) => sum + c.amount_cents, 0);

      const thisYearCosts = oneTimeCosts
        .filter((c: any) => new Date(c.date) >= startOfYear)
        .reduce((sum: number, c: any) => sum + c.amount_cents, 0);

      // Revenue from transaction-based P&L (income = positive transactions)
      const thisYearRevenue = thisYearPL.totals?.income || 0;
      const lastYearRevenue = lastYearPL.totals?.income || 0;

      // Plans count
      const draftPlans = plans.filter((p: any) => p.status === 'draft').length;
      const approvedPlans = plans.filter((p: any) => p.status === 'approved').length;

      // Top costs by category
      const categoryTotals: Record<string, number> = {};
      activeSubscriptions.forEach((s: any) => {
        const cat = s.category || 'Uncategorized';
        const monthly = getMonthlyEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
        categoryTotals[cat] = (categoryTotals[cat] || 0) + monthly;
      });
      const topCostsByCategory = Object.entries(categoryTotals)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      setData({
        companies: {
          total: companies.length,
          active: activeCompanies.length
        },
        people: {
          total: people.length,
          active: activePeople.length
        },
        subscriptions: {
          total: subscriptions.length,
          active: activeSubscriptions.length,
          monthlyRunRate,
          annualRunRate,
          upcomingRenewals
        },
        oneTimeCosts: {
          thisMonth: thisMonthCosts,
          thisYear: thisYearCosts,
          lastMonth: lastMonthCosts
        },
        revenue: {
          thisYear: thisYearRevenue,
          lastYear: lastYearRevenue
        },
        bankImports: {
          unmatchedTransactions: unmatchedTransactions.length || 0
        },
        plans: {
          draft: draftPlans,
          approved: approvedPlans
        },
        topCostsByCategory
      });
    } catch (e) {
      console.error('Failed to load overview data:', e);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-2">Holdings Overview</h1>
        <div className="flex items-center gap-2 text-neutral-500">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <h1 className="text-2xl font-semibold mb-6">Holdings Overview</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-300" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">Failed to Load Dashboard</h3>
            <p className="text-neutral-500 mb-4">{error || 'An unexpected error occurred.'}</p>
            <Button onClick={loadOverviewData} icon={<RefreshCw className="w-4 h-4" />} text="Try Again" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const monthOverMonthChange = data.oneTimeCosts.lastMonth > 0
    ? ((data.oneTimeCosts.thisMonth - data.oneTimeCosts.lastMonth) / data.oneTimeCosts.lastMonth) * 100
    : 0;

  const yoyRevenueChange = data.revenue.lastYear > 0
    ? ((data.revenue.thisYear - data.revenue.lastYear) / data.revenue.lastYear) * 100
    : 0;

  // Calculate net margin
  const totalCostsThisYear = data.oneTimeCosts.thisYear + data.subscriptions.annualRunRate;
  const netMargin = data.revenue.thisYear - totalCostsThisYear;

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Holdings Overview</h1>
          <p className="text-neutral-500">Track costs, revenue, and manage your portfolio</p>
        </div>
        <Button
          variant="outline"
          onClick={loadOverviewData}
          icon={<RefreshCw className="w-4 h-4" />}
          text="Refresh"
        />
      </div>

      {/* Alert Banner - Unmatched Transactions */}
      {data.bankImports.unmatchedTransactions > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="font-medium text-amber-900">
                {data.bankImports.unmatchedTransactions} unmatched transaction{data.bankImports.unmatchedTransactions !== 1 ? 's' : ''}
              </div>
              <div className="text-sm text-amber-700">Review and match imported bank transactions</div>
            </div>
          </div>
          <Button
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-100"
            onClick={() => navigate('/holdings/bank-imports/review')}
            text="Review Now"
          />
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          label="Monthly Run Rate"
          value={formatCents(data.subscriptions.monthlyRunRate)}
          hint={`${data.subscriptions.active} active subscriptions`}
        />
        <StatCard
          label="Annual Run Rate"
          value={formatCents(data.subscriptions.annualRunRate)}
          hint="Projected yearly cost"
        />
        <StatCard
          label="YTD Revenue"
          value={formatCents(data.revenue.thisYear)}
          hint={yoyRevenueChange !== 0 ? `${yoyRevenueChange > 0 ? '+' : ''}${yoyRevenueChange.toFixed(0)}% vs last year` : 'No prior year data'}
        />
        <StatCard
          label="Net Margin YTD"
          value={formatCents(netMargin)}
          hint={netMargin >= 0 ? 'Profitable' : 'Operating loss'}
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/holdings/portfolio')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold">{data.companies.active}</div>
                <div className="text-sm text-neutral-500">Companies</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/holdings/people')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold">{data.people.active}</div>
                <div className="text-sm text-neutral-500">People</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/holdings/planning')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Calculator className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold">{data.plans.draft + data.plans.approved}</div>
                <div className="text-sm text-neutral-500">Cost Plans</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate('/holdings/revenue/margins')}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-semibold">{data.companies.active}</div>
                <div className="text-sm text-neutral-500">Margin Analysis</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Run Rate Widget */}
        <div className="lg:col-span-2">
          <RunRateWidget
            monthlyRunRate={data.subscriptions.monthlyRunRate}
            annualRunRate={data.subscriptions.annualRunRate}
            activeSubscriptions={data.subscriptions.active}
            topCategories={data.topCostsByCategory}
          />
        </div>

        {/* One-Time Costs Summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">One-Time Costs</h2>
              <Button
                variant="outline"
                className="h-8 text-xs"
                onClick={() => navigate('/holdings/costs')}
                text="View All"
              />
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-neutral-50 rounded-xl">
                <div className="text-sm text-neutral-500 mb-1">This Month</div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-semibold">
                    {formatCents(data.oneTimeCosts.thisMonth)}
                  </span>
                  {monthOverMonthChange !== 0 && (
                    <span className={`flex items-center text-sm ${monthOverMonthChange > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {monthOverMonthChange > 0 ? (
                        <TrendingUp className="w-4 h-4 mr-1" />
                      ) : (
                        <TrendingDown className="w-4 h-4 mr-1" />
                      )}
                      {Math.abs(monthOverMonthChange).toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="text-xs text-neutral-400 mt-1">
                  vs {formatCents(data.oneTimeCosts.lastMonth)} last month
                </div>
              </div>

              <div className="p-4 bg-neutral-50 rounded-xl">
                <div className="text-sm text-neutral-500 mb-1">Year to Date</div>
                <div className="text-2xl font-semibold">
                  {formatCents(data.oneTimeCosts.thisYear)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Renewals & Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* Upcoming Renewals */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Upcoming Renewals</h2>
              <span className="text-sm text-neutral-500">Next 30 days</span>
            </div>

            {data.subscriptions.upcomingRenewals.length > 0 ? (
              <div className="space-y-3">
                {data.subscriptions.upcomingRenewals.map((sub: any) => (
                  <div
                    key={sub.id}
                    className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg cursor-pointer hover:bg-neutral-100 transition-colors"
                    onClick={() => navigate(`/holdings/costs/subscriptions/${sub.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900">{sub.vendor_name}</div>
                        <div className="text-sm text-neutral-500">
                          Renews {formatDate(sub.renewal_date)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-neutral-900">
                        {formatCents(sub.cost_cents)}
                      </div>
                      <div className="text-xs text-neutral-500">/{sub.billing_cycle}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-neutral-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                <p>No upcoming renewals</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>

            <div className="space-y-3">
              <button
                onClick={() => navigate('/holdings/portfolio')}
                className="w-full flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">Manage Companies</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400" />
              </button>

              <button
                onClick={() => navigate('/holdings/costs')}
                className="w-full flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">Track Costs</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400" />
              </button>

              <button
                onClick={() => navigate('/holdings/bank-imports/upload')}
                className="w-full flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">Import Bank Statement</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400" />
              </button>

              <button
                onClick={() => navigate('/holdings/revenue')}
                className="w-full flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">Track Revenue</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400" />
              </button>

              <button
                onClick={() => navigate('/holdings/planning')}
                className="w-full flex items-center justify-between p-4 bg-neutral-50 rounded-lg hover:bg-neutral-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">Cost Planning</span>
                </div>
                <ArrowRight className="w-4 h-4 text-neutral-400" />
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
